// React 
import { useState, useEffect } from 'react';

// Store
import useSiteStore from '../../store/siteStore.js';
import useProfileStore from '../../store/profileStore.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components 
import { AccessTime, AttachFile, DeleteOutlined, EditOutlined, EventNoteOutlined, LocalFireDepartmentOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, NavigateBeforeOutlined, NavigateNextOutlined, RadioButtonCheckedOutlined, CommentOutlined, } from '@mui/icons-material';
import { Box, Button, Card, Chip, Collapse, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, LinearProgress, Paper, Stack, Typography }
    from '@mui/material';

// Style
import { blue, green, grey, red, yellow, orange } from '@mui/material/colors/index.js';
import { defaultColorScheme, tealColorScheme, darkTheme } from '../../styles/colorSchemes/index.js';

// Page
import LoadingProgress from './LoadingProgress.js';
import EditTrainingSession from '../dialog/EditTrainingSession.js';
import Feedback from '../dialog/Feedback.js';

//Other 
import { deleteWeeklySchedule as deleteWSchedule } from '../../service/httpService/weeklySchedule/index.js';
import { formatCalendarWeekString, formatDate2, formatTrainingFocus, getWeek } from '../../utils/index.js';
import { deleteTrainingSessionByID as deleteTSession } from '../../service/httpService/trainingSession/index.js';

// Custom Components
import CustomSnackbar from './CustomSnackbar.js';

const WeeklyScheduleMobile = (props) => {
    const { deleteFilesAfterDownload, handleChangeTrainingSession, isEditable, isFeedbackEditable, selectedAthlete, isDeletable, isAthlete, showHeader = true } = props;
    const { changeCalenderWeek, feedbacks, highlightCurrentCalenderWeek, isWeeklyScheduleLoading, openEditTrainingSession, openFeedback, removeTrainingSession, selectedCalenderWeek,
        setOpenEditTrainingSession, setOpenFeedback, setSelectedCalenderWeek, weeklySchedule, year, setYear } = useSiteStore();
    const { colorScheme } = useProfileStore();
    
    // Theme Hook
    const { isDarkMode } = useAppTheme();

    const [selectedSession, setSelectedSession] = useState();
    const [openFail, setOpenFail] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    const [openSuccess, setOpenSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState();
    const [showFeedbackFiles, setShowFeedbackFiles] = useState(false);
    const [mapIdToIdx, setMapIdToIdx] = useState({});
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [feedbackOpen, setFeedbackOpen] = useState({});
    const [validWeekNumber, setValidWeekNumber] = useState(selectedCalenderWeek);
    const [displayWeekString, setDisplayWeekString] = useState('');

    // Ensure selectedCalenderWeek is valid, otherwise use current week
    useEffect(() => {
        const currentWeek = getWeek(new Date());
        if (!selectedCalenderWeek || isNaN(selectedCalenderWeek)) {
            setSelectedCalenderWeek(currentWeek);
            setValidWeekNumber(currentWeek);
        } else {
            setValidWeekNumber(selectedCalenderWeek);
        }
    }, [selectedCalenderWeek, setSelectedCalenderWeek]);

    // Update display week string when week or year changes
    useEffect(() => {
        if (validWeekNumber && year && !isNaN(validWeekNumber)) {
            const calculatedString = formatCalendarWeekString(validWeekNumber, year);
            setDisplayWeekString(calculatedString);
        }
    }, [validWeekNumber, year]);

    let colors = defaultColorScheme.trainingFocusColor;

    if (colorScheme === 'teal') {
        colors = tealColorScheme.trainingFocusColor;
    }

    // Logic for Week Navigator Colors in Dark Mode
    const currentDate = new Date();
    const currentWeek = getWeek(currentDate);
    const currentYear = currentDate.getFullYear();
    const isFuture = year > currentYear || (year === currentYear && validWeekNumber > currentWeek);
    const isPast = year < currentYear || (year === currentYear && validWeekNumber < currentWeek);
    const isCurrent = year === currentYear && validWeekNumber === currentWeek;

    useEffect(() => {
        if (weeklySchedule?.trainingSessions) {
            let dict = {};
            for (let idxTS = 0; idxTS < weeklySchedule.trainingSessions.length; idxTS++) {
                let tsID = weeklySchedule.trainingSessions[idxTS].trainingsessionid;
                dict[tsID] = 'no feedback yet';

                for (let idxFB = 0; idxFB < feedbacks.length; idxFB++) {
                    if (tsID === feedbacks[idxFB].trainingsessionid) {
                        dict[tsID] = idxFB;
                    }
                }
            }
            setMapIdToIdx(dict);
        }
    }, [weeklySchedule, feedbacks]);

    const deleteWeeklySchedule = () => {
        deleteWSchedule(selectedAthlete.profile.email, year, weeklySchedule.calendarWeek)
            .then(() => {
                // Instead of reloading, just remove from local state and close
                // The parent component (Dashboard) will handle refreshing athlete list via onRefreshUserLists
                setOpenSuccess(true);
                setSuccessMessage('Wochenplan erfolgreich gelöscht!');
                // Optional: reload after a short delay if needed, but prefer parent refresh
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            })
            .catch(() => {
                setOpenFail(true);
                setFailMessage('Löschen des Wochenplans fehlgeschlagen!');
            });
    }

    const deleteTrainingSession = (index) => {
        const sessionId = weeklySchedule.trainingSessions[index].trainingsessionid;
        deleteTSession(localStorage.getItem('email'), sessionId)
            .then(() => {
                removeTrainingSession(sessionId);
                setSuccessMessage('Trainingseinheit erfolgreich gelöscht!');
                setOpenSuccess(true);
            })
            .catch(() => {
                setFailMessage('Löschen der Trainingseinheit fehlgeschlagen!');
                setOpenFail(true);
            });
    }

    const isDeleteDisabled = (session) => {
        return (session.trainingFocus === 8 || session.trainingFocus === 7 || session.trainingFocus === 9 || session.trainingFocus === 10);
    };

    const disabledEdit = (trainingSession) => {
        if (!weeklySchedule) {
            return true;
        }
        let sessionDate = new Date(trainingSession.date).setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let now = new Date().setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let dif = now - sessionDate;
        return weeklySchedule.calendarWeek < getWeek(new Date()) || dif > 0;
    }

    const disabled = () => {
        if (!weeklySchedule) return true;
        return weeklySchedule.calendarWeek < getWeek(new Date());
    }

    const disabledFeedback = (trainingSession) => {
        if (!weeklySchedule)
            return true;
        if (!isFeedbackEditable)
            return !trainingSessionHasFeedback(trainingSession);
        let sessionDate = new Date(trainingSession.date).setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let now = new Date().setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let dif = now - sessionDate;
        return dif < 0 || dif > 10;
    }

    //Zeigt die farbcodierte Erschöpfung des Athleten an: grün = niedrig, gelb = mittel, rot = hoch, grau = keine Angabe
    const renderExhaustion = (exhaustion) => {
        const colorMap = {
            1: green[500],
            2: yellow[500],
            3: red[500],
        };

        return (
            <RadioButtonCheckedOutlined sx={{ color: colorMap[exhaustion] || grey[500] }} />
        );
    };

    const getTimeString = (time) => {
        if (time != null) {
            return time.slice(0, time.lastIndexOf(':'));
        }
    }

    const trainingSessionHasFeedback = (trainingSession) => {
        return mapIdToIdx[trainingSession.trainingsessionid] !== undefined && mapIdToIdx[trainingSession.trainingsessionid] !== 'no feedback yet'
            && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]] !== undefined;
    }

    // Common KW Navigator Component
    const KWNavigator = () => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2,
            px: 1
        }}>
            <Paper
                elevation={0}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    bgcolor: isDarkMode ? '#1e1e1e' : 'background.paper',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    maxWidth: '100%',
                    '&:hover': {
                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
                    }
                }}
            >
                <IconButton
                    onClick={() => changeCalenderWeek(false)}
                    sx={{
                        borderRadius: 0,
                        px: 1,
                        py: 0.75,
                        color: isDarkMode 
                            ? (isFuture ? orange[500] : darkTheme.textSecondary)
                            : highlightCurrentCalenderWeek(selectedCalenderWeek, "less"),
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50]
                        }
                    }}
                >
                    <NavigateBeforeOutlined sx={{ fontSize: 20 }} />
                </IconButton>

                <Box
                    onClick={() => {
                        const currentDate = new Date();
                        setYear(currentDate.getFullYear());
                        setSelectedCalenderWeek(getWeek(currentDate));
                    }}
                    sx={{
                        px: 2,
                        py: 0.75,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'center',
                        '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50]
                        }
                    }}
                >
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                color: isDarkMode 
                                    ? (isCurrent ? orange[500] : darkTheme.textPrimary)
                                    : highlightCurrentCalenderWeek(selectedCalenderWeek, "equal"),
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            KW {validWeekNumber}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: 'none' }}
                        >
                            •
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDarkMode ? darkTheme.textSecondary : 'text.secondary',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {displayWeekString}
                        </Typography>
                    </Stack>
                </Box>

                <IconButton
                    onClick={() => changeCalenderWeek(true)}
                    sx={{
                        borderRadius: 0,
                        px: 1,
                        py: 0.75,
                        color: isDarkMode 
                            ? (isPast ? orange[500] : darkTheme.textSecondary)
                            : highlightCurrentCalenderWeek(selectedCalenderWeek, "more"),
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : blue[50]
                        }
                    }}
                >
                    <NavigateNextOutlined sx={{ fontSize: 20 }} />
                </IconButton>
            </Paper>
        </Box>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <CustomSnackbar 
                open={openFail} 
                onClose={() => setOpenFail(false)} 
                message={failMessage} 
                severity="error" 
                autoHideDuration={4000}
            />
            <CustomSnackbar 
                open={openSuccess} 
                onClose={() => setOpenSuccess(false)} 
                message={successMessage} 
                severity="success" 
                autoHideDuration={4000}
            />
            {weeklySchedule !== undefined && selectedSession !== undefined && weeklySchedule !== "" && (
                <>
                    <Feedback
                        deleteFilesAfterDownload={deleteFilesAfterDownload}
                        selectedFeedback={selectedFeedback}
                        setSelectedFeedback={setSelectedFeedback}
                        isFeedbackEditable={isFeedbackEditable}
                        open={openFeedback}
                        onClose={() => setOpenFeedback(false)}
                        trainingSession={selectedSession}
                        athlete={selectedAthlete}
                        showFeedbackFiles={showFeedbackFiles} />

                    {isEditable === true && (
                        <EditTrainingSession
                            handleChangeTrainingSession={handleChangeTrainingSession}
                            athlete={selectedAthlete}
                            calendarWeek={weeklySchedule.calendarWeek}
                            setSelectedSession={setSelectedSession}
                            trainingSession={selectedSession}
                            isAthlete={isAthlete}
                            open={openEditTrainingSession}
                            onClose={() => setOpenEditTrainingSession(false)} />
                    )}
                </>
            )}

            {/* Render KW Navigator if showHeader is true */}
            {showHeader && (isEditable === true && selectedAthlete !== null && selectedAthlete !== undefined ? (
                <KWNavigator />
            ) : isEditable === false ? (
                <KWNavigator />
            ) : null)}

            <Container>
                {(weeklySchedule === '' || weeklySchedule === undefined) && !isWeeklyScheduleLoading && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                        px: 2,
                        minHeight: '300px'
                    }}>
                        <Box sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <EventNoteOutlined sx={{ fontSize: 60, color: grey[400] }} />
                        </Box>
                        <Typography 
                            variant='h6' 
                            fontWeight={600} 
                            mb={1} 
                            sx={{ 
                                fontSize: { xs: '1.1rem', sm: '1.15rem', md: '1.25rem' },
                                color: isDarkMode ? 'white' : 'text.secondary' 
                            }}
                        >
                            Kein Wochenplan vorhanden
                        </Typography>
                        <Typography 
                            variant='body2' 
                            sx={{ 
                                textAlign: 'center', 
                                maxWidth: { xs: 300, sm: 350, md: 400 }, 
                                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                color: isDarkMode ? 'white' : 'text.secondary' 
                            }}
                        >
                            Für diese Woche wurde noch kein Trainingsplan erstellt.
                        </Typography>
                    </Box>
                )}

                {isWeeklyScheduleLoading && (<LoadingProgress />)}

                {weeklySchedule !== '' && weeklySchedule !== undefined && weeklySchedule.trainingSessions !== undefined && (
                    weeklySchedule.trainingSessions.sort((a, b) => {
                        if (a.dayOfWeek < b.dayOfWeek)
                            return -1;          // first sort by dayOfWeek, then by trainingFocus
                        if (a.dayOfWeek > b.dayOfWeek)
                            return 1;
                        if (a.trainingFocus < b.trainingFocus)
                            return -1;
                        if (a.trainingFocus > b.trainingFocus)
                            return 1;
                        return 0;
                    })
                        .map((trainingSession, index) => (
                            <Card key={index} sx={{
                                mt: 2,
                                mb: 2,
                                borderRadius: '12px',
                                boxShadow: isDarkMode ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.2s ease',
                                borderLeft: `5px solid ${colors[trainingSession.trainingFocus - 1]}`,
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                overflow: 'hidden',
                                '&:hover': {
                                    boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                                }
                            }}>
                                {/* Main Content */}
                                <Box sx={{ p: 1.5 }}>
                                    {/* Header Row with Edit Button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant='subtitle1'
                                                fontWeight={700}
                                                sx={{
                                                    color: colors[trainingSession.trainingFocus - 1],
                                                    fontSize: '1rem',
                                                    mb: 0.5
                                                }}
                                            >
                                                {formatTrainingFocus(trainingSession.trainingFocus)}
                                            </Typography>
                                            <Typography variant='caption' sx={{ fontSize: '0.75rem', color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                {formatDate2(trainingSession.date)}
                                            </Typography>
                                        </Box>

                                        {/* Action Buttons */}
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            {/* Feedback Chip */}
                                            {trainingSessionHasFeedback(trainingSession) && (
                                                <Chip
                                                    icon={feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].hasNewFiles ? <AttachFile sx={{ fontSize: 16, color: '#ff9800 !important' }} /> : <CommentOutlined sx={{ fontSize: 16, color: '#ff9800 !important' }} />}
                                                    label={feedbackOpen[index] ? <KeyboardArrowUpOutlined sx={{ fontSize: 18 }} /> : <KeyboardArrowDownOutlined sx={{ fontSize: 18 }} />}
                                                    onClick={() => setFeedbackOpen(prev => ({ ...prev, [index]: !prev[index] }))}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'transparent',
                                                        color: orange[700],
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: orange[50]
                                                        },
                                                        '& .MuiChip-label': {
                                                            px: 0.5,
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        },
                                                        '& .MuiChip-icon': {
                                                            color: '#ff9800 !important'
                                                        }
                                                    }}
                                                />
                                            )}

                                            {/* Edit Button */}
                                            {isEditable && !trainingSessionHasFeedback(trainingSession) && (
                                                trainingSession.trainingFocus === 10 && !isAthlete ? (
                                                    // Trainers can only delete Sonstige Aktivität, not edit
                                                    <IconButton
                                                        disabled={disabledEdit(trainingSession)}
                                                        onClick={() => deleteTrainingSession(weeklySchedule.trainingSessions.findIndex(s => s.trainingsessionid === trainingSession.trainingsessionid))}
                                                        size="small"
                                                        sx={{
                                                            color: red[500],
                                                            '&:disabled': {
                                                                color: isDarkMode ? grey[700] : grey[300]
                                                            }
                                                        }}
                                                    >
                                                        <DeleteOutlined fontSize="small" />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        disabled={disabledEdit(trainingSession)}
                                                        onClick={() => {
                                                            setOpenEditTrainingSession(true);
                                                            setSelectedSession(trainingSession);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            color: isDarkMode ? darkTheme.accent : 'primary.main',
                                                            '&:disabled': {
                                                                color: isDarkMode ? grey[700] : grey[300]
                                                            }
                                                        }}
                                                    >
                                                        <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                )
                                            )}

                                            {/* Feedback Button */}
                                            {!isEditable && isFeedbackEditable && (
                                                <IconButton
                                                    disabled={disabledFeedback(trainingSession)}
                                                    onClick={() => {
                                                        setOpenFeedback(true);
                                                        setSelectedSession(trainingSession);
                                                        setSelectedFeedback(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]]);
                                                        setShowFeedbackFiles(false);
                                                    }}
                                                    size="small"
                                                    sx={{
                                                        color: isDarkMode ? darkTheme.accent : 'primary.main',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(25, 118, 210, 0.08)'
                                                        },
                                                        '&:disabled': {
                                                            color: isDarkMode ? grey[700] : grey[300]
                                                        }
                                                    }}
                                                >
                                                    <EditOutlined fontSize="small" />
                                                </IconButton>
                                            )}

                                            {/* Delete Button */}
                                            {isAthlete && isDeletable && (
                                                <IconButton
                                                    onClick={() => deleteTrainingSession(index)}
                                                    disabled={!isDeleteDisabled(trainingSession)}
                                                    size="small"
                                                    sx={{
                                                        color: red[500],
                                                        '&:disabled': {
                                                            color: isDarkMode ? grey[700] : grey[300]
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlined fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Stats Row - hide for focus 7, 8, 9, 10 */}
                                    {![7, 8, 9, 10].includes(trainingSession.trainingFocus) && (
                                        <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AccessTime sx={{ fontSize: 18, color: grey[500] }} />
                                                <Typography variant='body2' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                                    {trainingSession.duration} min
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 80 }}>
                                                <LocalFireDepartmentOutlined sx={{ fontSize: 20, color: grey[500] }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant='caption' sx={{ fontSize: '0.7rem', fontWeight: 700, color: isDarkMode ? darkTheme.textPrimary : 'text.primary' }}>
                                                        {trainingSession.intensity}/10
                                                    </Typography>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(trainingSession.intensity / 10) * 100}
                                                        sx={{
                                                            height: 4,
                                                            borderRadius: 2,
                                                            bgcolor: grey[200],
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: colors[trainingSession.trainingFocus - 1]
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Description */}
                                    {trainingSession.description && (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, maxWidth: '100%' }}>
                                            <Typography variant='caption' sx={{ fontWeight: 600, fontSize: '0.7rem', flexShrink: 0, color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                Beschreibung:
                                            </Typography>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: isDarkMode ? darkTheme.textSecondary : grey[600],
                                                    flex: 1,
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {expandedDescriptions[index]
                                                    ? trainingSession.description
                                                    : trainingSession.description.length > 30
                                                        ? trainingSession.description.substring(0, 30) + '...'
                                                        : trainingSession.description
                                                }
                                            </Typography>
                                            {trainingSession.description.length > 30 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setExpandedDescriptions(prev => ({ ...prev, [index]: !prev[index] }))}
                                                    sx={{
                                                        p: 0.3,
                                                        color: colors[trainingSession.trainingFocus - 1],
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {expandedDescriptions[index] ? <KeyboardArrowUpOutlined sx={{ fontSize: 16 }} /> : <KeyboardArrowDownOutlined sx={{ fontSize: 16 }} />}
                                                </IconButton>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {/* Collapsible Feedback Section */}
                                <Collapse in={feedbackOpen[index]} timeout="auto" unmountOnExit>
                                    {trainingSessionHasFeedback(trainingSession) && selectedAthlete !== null && selectedAthlete !== undefined && (
                                        <Box sx={{
                                            p: 2,
                                            pl: 3,
                                            ml: 1,
                                            mr: 1,
                                            mb: 1,
                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[50],
                                            borderLeft: `3px solid ${colors[trainingSession.trainingFocus - 1]}`,
                                            borderRadius: '0 0 8px 8px'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant='subtitle2' fontWeight={700} color={green[700]}>Rückmeldung</Typography>
                                                    {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].hasNewFiles && (
                                                        <IconButton
                                                            onClick={() => {
                                                                setOpenFeedback(true);
                                                                setSelectedSession(trainingSession);
                                                                setSelectedFeedback(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]]);
                                                                setShowFeedbackFiles(true);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                color: '#ff9800',
                                                                p: 0.5,
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(255, 152, 0, 0.1)'
                                                                }
                                                            }}>
                                                            <AttachFile sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].lastEdited !== null && (
                                                    <Typography variant='caption' sx={{ fontSize: '0.7rem', color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                        {formatDate2(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].lastEdited)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AccessTime sx={{ fontSize: 16, color: grey[500] }} />
                                                        <Typography variant='body2' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].actualDuration} min
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <LocalFireDepartmentOutlined sx={{ fontSize: 18, color: grey[500] }} />
                                                        <Typography variant='body2' fontWeight={700} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].actualIntensity}/10
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Erschöpfung:</Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            {renderExhaustion(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].physicalExhaustion)}
                                                            {renderExhaustion(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].mentalExhaustion)}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].comment && (
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography variant='caption' fontWeight={600} display='block' mb={0.5} color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>
                                                        Kommentar
                                                    </Typography>
                                                    <Typography variant='body2' sx={{ lineHeight: 1.6, fontSize: '0.85rem', color: isDarkMode ? darkTheme.textPrimary : 'text.primary' }}>
                                                        {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].comment}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {selectedAthlete.hasExtendedFeedback && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback && (
                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1, borderTop: `1px solid ${grey[200]}` }}>
                                                    {/* Extended Feedback Items ... (keeping styling consistent implicitly or adding color props) */}
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Weckzeit:</Typography>
                                                        <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                                            {getTimeString(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.wakeUpTime)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Schlafdauer:</Typography>
                                                        <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.sleepDuration} h
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Ruhepuls:</Typography>
                                                        <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.restingPulse} bpm
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Gewicht:</Typography>
                                                        <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : undefined}>
                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.weight} kg
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Collapse>
                            </Card>
                        ))
                )}
                {isEditable === true && weeklySchedule !== undefined && weeklySchedule !== '' && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={disabled()}
                            onClick={() => setOpenConfirmation(true)}
                            startIcon={<DeleteOutlined />}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                '&:hover': {
                                    bgcolor: red[50]
                                }
                            }}
                        >
                            Wochenplan löschen
                        </Button>
                    </Box>
                )
                }
            </Container>

            {weeklySchedule !== undefined && weeklySchedule !== '' && (
                <Dialog open={openConfirmation} onClose={() => setOpenConfirmation(false)} PaperProps={{ sx: { bgcolor: isDarkMode ? darkTheme.cardBg : 'white' } }}>

                    <DialogTitle sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                        Löschen Bestätigen
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }}>
                            Sind Sie sich sicher, dass dieser Wochenplan
                            (KW{weeklySchedule.calendarWeek}: {formatCalendarWeekString(weeklySchedule.calendarWeek, weeklySchedule.year)})
                            gelöscht werden soll?
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button sx={{ color: 'red', '&:hover': { background: 'rgba(255, 249, 196, 0.3)' } }} onClick={() => { setOpenConfirmation(false); deleteWeeklySchedule() }}>
                            Löschen
                        </Button>

                        <Button sx={{ color: isDarkMode ? darkTheme.textPrimary : 'black', '&:hover': { background: 'rgba(255, 249, 196, 0.3)' } }} onClick={() => setOpenConfirmation(false)}>
                            Abbrechen
                        </Button>
                    </DialogActions>

                </Dialog>
            )
            }
        </Box>
    )
}

export default WeeklyScheduleMobile;